import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiPath from "../../utils/apiPath";


/* Helper function to calculate expiration time */
export const getExpirationTime = (expiresInMinutes) => Date.now() + (expiresInMinutes * 60 * 1000);
export const REFRESH_REPORT_TIME=30; //MINUTES
export const DEFAULT_REFRESH_TIME_MS = REFRESH_REPORT_TIME * 60 * 1000;


/* Helper function to get token expiry time from localStorage */
const getTokenExpiryTime = () => {
    const expirationTime = localStorage.getItem("tokenExpiration");
    return expirationTime ? parseInt(expirationTime, 10) : null;
};

/* Helper function to check if token is expired */
const isTokenExpired = () => {
    const expirationTime = getTokenExpiryTime();
    return expirationTime && Date.now() > expirationTime;
};

/* Helper function to check if refresh is needed (5 minutes before expiry) */
const isRefreshNeeded = () => {
    const expirationTime = getTokenExpiryTime();
    if (!expirationTime) return false;
    const timeUntilExpiry = expirationTime - Date.now();
    return timeUntilExpiry <= 5 * 60 * 1000; // 5 minutes in milliseconds
};

/* Refresh token interval reference */
let refreshTokenInterval = null;

/* Setup refresh token interval */
const setupRefreshTokenInterval = (dispatch) => {
    // Clear existing interval if any
    if (refreshTokenInterval) {
        clearInterval(refreshTokenInterval);
        refreshTokenInterval = null;
    }

    // Set up new interval to refresh token 5 minutes before expiry
    refreshTokenInterval = setInterval(async () => {
        const token = localStorage.getItem("token");
        if (token && !isTokenExpired() && isRefreshNeeded()) {
            try {
                await dispatch(refreshToken()).unwrap();
                console.log("Token refreshed automatically (5 minutes before expiry)");
            } catch (error) {
                console.error("Auto token refresh failed:", error);
                // Clear interval on failure
                clearRefreshTokenInterval();
            }
        }
    }, 60 * 1000); // Check every minute
};

/* Clear refresh token interval */
const clearRefreshTokenInterval = () => {
    if (refreshTokenInterval) {
        clearInterval(refreshTokenInterval);
        refreshTokenInterval = null;
    }
};

/* Refresh Token */
export const refreshToken = createAsyncThunk(
    "auth/refreshToken",
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const refreshTokenValue = localStorage.getItem("refreshToken");
            if (!refreshTokenValue) {
                throw new Error("No refresh token available");
            }

            const response = await apiPath.post("/api/v1.0/auth/refresh-token", {
                refreshToken: refreshTokenValue
            });
            
            console.log(response, 'refresh');
            const newToken = response.data?.data?.token;
            const newRefreshToken = response.data?.data?.refreshToken;
            const expiresInMinutes = response.data?.data?.expiry_at;

            if (newToken && expiresInMinutes !== undefined) {
                const expirationTime = getExpirationTime(expiresInMinutes);
                
                localStorage.setItem("token", newToken);
                localStorage.setItem("tokenExpiration", expirationTime.toString());
                
                if (newRefreshToken) {
                    localStorage.setItem("refreshToken", newRefreshToken);
                }

                // Update store with new token
                dispatch(setToken(newToken));
                
                return { 
                    token: newToken, 
                    refreshToken: newRefreshToken,
                    expiresIn: expiresInMinutes
                };
            }

            throw new Error("Invalid refresh token response");
        } catch (error) {
            // If refresh fails, logout user
            dispatch(logout());
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/* Login */
export const login = createAsyncThunk(
    "auth/login",
    async (userData, { rejectWithValue, dispatch }) => {
        try {
            const response = await apiPath.post("/api/v1.0/auth/login", userData);
            
            const token = response.data?.data?.token;
            const refreshTokenValue = response.data?.data?.refreshToken;
            const userDataResponse = response.data?.data?.userData;
            const expiresInMinutes = response.data?.data?.expiry_at;

            if (token && expiresInMinutes !== undefined) {
                const expirationTime = getExpirationTime(expiresInMinutes);
                
                localStorage.setItem("token", token);
                localStorage.setItem("loginUserData", JSON.stringify(userDataResponse));
                localStorage.setItem("tokenExpiration", expirationTime.toString());
                
                if (refreshTokenValue) {
                    localStorage.setItem("refreshToken", refreshTokenValue);
                }

                // Setup auto refresh interval
                setupRefreshTokenInterval(dispatch);
                
                return { 
                    ...response.data, 
                    token, 
                    refreshToken: refreshTokenValue, 
                    userData: userDataResponse,
                    expiresIn: expiresInMinutes
                };
            }

            throw new Error("Invalid login response");
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);



/* Register */
export const register = createAsyncThunk(
    "auth/register",
    async (userData, { rejectWithValue }) => {
        try {
            const response = await apiPath.post("/api/v1.0/auth/register", userData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/* Update Password */
export const updatePassword = createAsyncThunk(
    "auth/updatePassword",
    async (userData, { rejectWithValue }) => {
        try {
            const response = await apiPath.post("/api/v1.0/auth/update-password", userData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/* Forget Password */
export const forgetPassword = createAsyncThunk(
    "auth/forgetPassword",
    async (userData, { rejectWithValue }) => {
        try {
            const response = await apiPath.post("/api/v1.0/auth/forget-password", userData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// FCM Token Push Notification
export const fcmDeviceToken = createAsyncThunk(
    "device-tokens/save",
    async (userData, { rejectWithValue }) => {
        try {
            const response = await apiPath.post("/api/v1.0/fcm-device-tokens/save", userData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

/* Check and refresh token before API calls - ONLY if 5 minutes or less remaining */
export const ensureValidToken = () => async (dispatch, getState) => {
    const token = localStorage.getItem("token");
    
    // If no token, don't proceed
    if (!token) {
        throw new Error("No token available");
    }
    
    // If token is expired, don't proceed
    if (isTokenExpired()) {
        throw new Error("Token has expired");
    }
    
    // Only refresh if we're within 5 minutes of expiry
    if (isRefreshNeeded()) {
        try {
            await dispatch(refreshToken()).unwrap();
            console.log("Token refreshed before API call (5 minutes remaining)");
        } catch (error) {
            console.error("Token refresh before API call failed:", error);
            throw error;
        }
    }
    // If more than 5 minutes remaining, continue with current token
};

// Utility to get current token state
export const getTokenStatus = () => {
    const token = localStorage.getItem("token");
    const expiryTime = getTokenExpiryTime();
    
    if (!token || !expiryTime) {
        return { isValid: false, message: "No token available" };
    }
    
    const timeUntilExpiry = expiryTime - Date.now();
    const minutesUntilExpiry = Math.round(timeUntilExpiry / 1000 / 60);
    
    return {
        isValid: timeUntilExpiry > 0,
        expiresIn: `${minutesUntilExpiry} minutes`,
        needsRefresh: isRefreshNeeded(),
        isExpired: isTokenExpired()
    };
};

/* ----------------- DYNAMIC REFRESH TIME FUNCTIONS (ADD THIS) ----------------- */
/* Get refresh interval for specific user/report */
export const getUserReportRefreshInterval = (userId, reportId) => {
    try {
        const userReportSetting = localStorage.getItem(`report_refresh_${reportId}_${userId}`);
        if (userReportSetting && userReportSetting !== "0") {
            return parseInt(userReportSetting, 10) * 1000;
        }
        
        const userDefault = localStorage.getItem(`user_default_refresh_${userId}`);
        if (userDefault) {
            return parseInt(userDefault, 10) * 1000 * 60;
        }
        
        return DEFAULT_REFRESH_TIME_MS;
    } catch (error) {
        console.error("Error getting refresh interval:", error);
        return DEFAULT_REFRESH_TIME_MS;
    }
};

/* Get all refresh settings for a user */
export const getUserRefreshSettings = (userId) => {
    try {
        const settings = {};
        const reports = [
            'booking', 'stp', 'geofence', 'unassigned', 
            'booking-detail', 'activity', 'rfid', 
            'heatmap', 'location-mismatch', 'dashboard'
        ];
        
        reports.forEach(reportId => {
            const value = localStorage.getItem(`report_refresh_${reportId}_${userId}`);
            if (value) {
                settings[reportId] = parseInt(value, 10);
            }
        });
        
        return settings;
    } catch (error) {
        console.error("Error getting user refresh settings:", error);
        return {};
    }
};

/* Clear user refresh settings */
export const clearUserRefreshSettings = (userId) => {
    try {
        const reports = [
            'booking', 'stp', 'geofence', 'unassigned', 
            'booking-detail', 'activity', 'rfid', 
            'heatmap', 'location-mismatch', 'dashboard'
        ];
        
        reports.forEach(reportId => {
            localStorage.removeItem(`report_refresh_${reportId}_${userId}`);
        });
        localStorage.removeItem(`user_default_refresh_${userId}`);
        
        return true;
    } catch (error) {
        console.error("Error clearing refresh settings:", error);
        return false;
    }
};

/* Auth Slice */
const authSlice = createSlice({
    name: "auth",
    initialState: {
        token: isTokenExpired() ? null : localStorage.getItem("token"),
        refreshToken: localStorage.getItem("refreshToken"),
        loginUserData: isTokenExpired()
            ? null
            : JSON.parse(localStorage.getItem("loginUserData") || "null"),
        loading: false,
        error: null,
        isRefreshing: false,
    },
    reducers: {
        logout: (state) => {
            state.token = null;
            state.refreshToken = null;
            state.loginUserData = null;
            localStorage.removeItem("loginUserData");
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("tokenExpiration");
            localStorage.removeItem("CARX_TOKEN");
            
            // Clear refresh interval
            clearRefreshTokenInterval();
        },
        setToken: (state, action) => {
            state.token = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        initializeAuth: (state, action) => {
            const { dispatch } = action.payload;
            // Setup refresh interval on app start if token exists
            if (localStorage.getItem("token") && !isTokenExpired()) {
                setupRefreshTokenInterval(dispatch);
            }
        },
        checkTokenStatus: (state) => {
        }
    },
    extraReducers: (builder) => {
        builder
            /* Login */
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                state.loginUserData = action.payload.userData;
                state.token = action.payload.token;
                state.refreshToken = action.payload.refreshToken;
                state.error = null;
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.payload;
            })
            
            /* Refresh Token */
            .addCase(refreshToken.pending, (state) => {
                state.isRefreshing = true;
                state.error = null;
            })
            .addCase(refreshToken.fulfilled, (state, action) => {
                state.isRefreshing = false;
                state.token = action.payload.token;
                if (action.payload.refreshToken) {
                    state.refreshToken = action.payload.refreshToken;
                }
                state.error = null;
            })
            .addCase(refreshToken.rejected, (state, action) => {
                state.isRefreshing = false;
                state.error = action.payload?.message || action.payload;
                // Auto logout on refresh failure
                state.token = null;
                state.refreshToken = null;
                state.loginUserData = null;
                clearRefreshTokenInterval();
            })
            
            /* Register */
            .addCase(register.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(register.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(register.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.payload;
            })
            
            /* Update Password */
            .addCase(updatePassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updatePassword.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(updatePassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.payload;
            })
            
            /* Forget Password */
            .addCase(forgetPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(forgetPassword.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(forgetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fcmDeviceToken.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(fcmDeviceToken.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.payload;
            });
    },
});

export const { logout, setToken, clearError, initializeAuth, checkTokenStatus } = authSlice.actions;
export default authSlice.reducer;